import { ISession } from "../types/dashboard";
import { sendRequest } from "./api";
import { dateToTimezoneAwareDatetime, parseTimezoneAwareDatetime } from "../utils/timezone";

interface IServerSession{
    ClientId: string,
    startDatetime?: string, // New timezone-aware datetime format
    endDatetime?: string,   // New timezone-aware datetime format
    Datetime?: number, // Legacy format for backward compatibility
    repeat?: number, //0, count session to create each time
    repeatEvery?: number, //0, repeat interval multiplier
    repeatInterval?: string | "day" | "week" | "month", //"day" | "week" | "month"
}

const clientToServerSession = (data: ISession): IServerSession=>{
    const result: any = {
        ...data,
        ClientId: data.client
    };
    
    // Use new timezone-aware datetime fields if available
    if (data.startDatetime && data.endDatetime) {
        result.startDatetime = data.startDatetime;
        result.endDatetime = data.endDatetime;
        // ALWAYS include legacy Datetime field for backend compatibility
        result.Datetime = Math.floor(new Date(data.startDatetime).valueOf() / 1000);
        result.Timestamp = Math.floor(new Date(data.startDatetime).valueOf() / 1000);
    } 
    // Fallback to legacy fields for backward compatibility
    else if (data.date && data.dateTo) {
        result.Datetime = Math.floor(new Date(data.date).valueOf() / 1000);
        result.Timestamp = Math.floor(new Date(data.date).valueOf() / 1000);
        // Also send timezone-aware versions converted from legacy fields
        try {
            result.startDatetime = dateToTimezoneAwareDatetime(new Date(data.date));
            result.endDatetime = dateToTimezoneAwareDatetime(new Date(data.dateTo));
        } catch (e) {
            console.warn('Failed to convert legacy date to timezone-aware format:', e);
        }
    }
    
    // Clean up client reference
    delete result.client;
    return result;
}

export const serverToClientSession = (data: IServerSession & ISession & {Client: string}): ISession=>{
    const result: any = {
        ...data,
        client: data.ClientId || data.Client || data.client
    };
    
    // Prefer new timezone-aware datetime fields
    if (data.startDatetime && data.endDatetime) {
        result.startDatetime = data.startDatetime;
        result.endDatetime = data.endDatetime;
        
        // Also provide legacy fields for backward compatibility during migration
        try {
            result.date = new Date(data.startDatetime).toISOString();
            result.dateTo = new Date(data.endDatetime).toISOString();
        } catch (e) {
            console.warn('Failed to convert timezone-aware datetime to legacy format:', e);
        }
    } 
    // Fallback to legacy conversion if new fields are not available
    else if (data.Datetime || data.date) {
        const restoredDate = new Date(typeof data.Datetime == 'number' ? (data.Datetime * 1000) : (data.Datetime || data.date));
        if (Number.isNaN(restoredDate.valueOf())){
            throw new Error('bad date');
        }
        
        let restoredDateTo: Date;
        if (data.dateTo) {
            const diffDate = new Date(data.dateTo).valueOf() - new Date(data.date || 0).valueOf();
            restoredDateTo = new Date(restoredDate.valueOf() + diffDate);
        } else {
            // Default to 1 hour session if no end date
            restoredDateTo = new Date(restoredDate.valueOf() + 1 * 60 * 60 * 1000);
        }
        
        if (Number.isNaN(restoredDateTo.valueOf())){
            console.log(data);
            restoredDateTo = new Date(restoredDate.valueOf() + 1 * 60 * 60 * 1000);
        }
        
        // Set legacy fields
        result.date = restoredDate.toISOString();
        result.dateTo = restoredDateTo.toISOString();
        
        // Try to convert to new timezone-aware format
        try {
            result.startDatetime = dateToTimezoneAwareDatetime(restoredDate);
            result.endDatetime = dateToTimezoneAwareDatetime(restoredDateTo);
        } catch (e) {
            console.warn('Failed to convert legacy date to timezone-aware format:', e);
        }
    }
    
    // Clean up server-specific fields
    delete result.Client;
    delete result.ClientId;
    return result;
}

export const postSession = async(data: any)=>{
    const result = await sendRequest('session', 'POST', clientToServerSession(data), '');
    // Only clear specific client cache, not all caches
    const clientId = data.client;
    if (clientId) {
        console.log('Clearing cache for client:', clientId);
        localStorage.removeItem(`cached_client_sessions_${clientId}`);
        localStorage.removeItem(`cached_client_sessions_timestamp_${clientId}`);
    }
    return result;
}

export const putSession = async(data: any,  clientId: string)=>{
    const result = await sendRequest('session/', 'PUT', {...clientToServerSession(data), "editOtherEvents": false}, clientId);
    // Only clear specific client cache
    localStorage.removeItem(`cached_client_sessions_${clientId}`);
    localStorage.removeItem(`cached_client_sessions_timestamp_${clientId}`);
    return result;
}

export const getSessions = async(clientId?:string, limit?:number, lastDate?:string, lastId?:string)=>{
    let endpoint = clientId ? `session/${clientId}` : 'session';
    
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (lastDate) params.append('lastdate', lastDate);
    if (lastId) params.append('lastid', lastId);
    
    if (params.toString()) {
        endpoint += (endpoint.includes('?') ? '&' : '?') + params.toString();
    }
    
    return sendRequest(endpoint, 'GET', undefined, '');
}

export const getSessionsWithResults = async(clientId?:string, limit?:number, lastDate?:string, lastId?:string)=>{
    let endpoint = clientId ? `session/${clientId}?result=true` : 'session?result=true';
    
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (lastDate) params.append('lastdate', lastDate);
    if (lastId) params.append('lastid', lastId);
    
    if (params.toString()) {
        endpoint += (endpoint.includes('?') ? '&' : '?') + params.toString();
    }
    
    return sendRequest(endpoint, 'GET', undefined, '');
}

export const deleteSession = async(session_id: string)=>{  
    /* action*  string
    Deletion action, e.g., 'delete-single', 'delete-all', 'delete-after', 'delete-before', 'delete-amount-before', 'delete-amount-after'

    includeThis boolean
    Include the current session in deletion

    amount      integer
    Number of sessions to delete (only for 'delete-amount-before' and 'delete-amount-after')*/
    const result = await sendRequest('session/', 'DELETE', {
        action: 'delete-single',
        includeThis: true,
        amount: 0
    }, session_id);
    // Only clear client caches, not schedule cache
    Object.keys(localStorage).forEach(key => {
        if (key.startsWith('cached_client_sessions_')) {
            localStorage.removeItem(key);
        }
    });
    return result;
}

export const getFutureSessions = async(clientId: string) => {
    try {
        const response = await getSessions(clientId);
        const data = await response.json();
        
        console.log('[getFutureSessions] Raw API response:', data);
        
        // Convert response to array format
        let sessionsArray: any[] = [];
        if (Array.isArray(data)) {
            // Handle array format: [{sessionId: sessionData}, ...]
            sessionsArray = data.flatMap(item => {
                // Each item is an object with session ID as key
                return Object.entries(item).map(([id, sessionData]) => ({
                    id,
                    ...(sessionData as any)
                }));
            });
        } else {
            // Handle object format: {sessionId: sessionData, ...}
            sessionsArray = Object.entries(data).map(([id, sessionData]) => ({
                id,
                ...(sessionData as any)
            }));
        }
        
        console.log('[getFutureSessions] Sessions array length:', sessionsArray.length);
        console.log('[getFutureSessions] First session sample:', sessionsArray[0]);
        
        // Filter for future sessions only
        const now = new Date();
        console.log('[getFutureSessions] Current time:', now.toISOString());
        
        const futureSessions = sessionsArray
            .map(session => serverToClientSession(session))
            .filter(session => {
                try {
                    // Use the proper timezone parser for startDatetime
                    let sessionDate: Date;
                    if (session.startDatetime) {
                        sessionDate = parseTimezoneAwareDatetime(session.startDatetime);
                    } else if (session.date) {
                        sessionDate = new Date(session.date);
                    } else {
                        console.log('[getFutureSessions] Session has no date:', session);
                        return false;
                    }
                    
                    const isFuture = sessionDate > now;
                    console.log('[getFutureSessions] Session:', {
                        id: session.id,
                        title: session.title,
                        startDatetime: session.startDatetime,
                        parsedDate: sessionDate.toISOString(),
                        isFuture
                    });
                    
                    return isFuture;
                } catch (e) {
                    console.error('[getFutureSessions] Failed to parse session date:', e, session);
                    return false;
                }
            })
            .sort((a, b) => {
                const dateA = a.startDatetime ? parseTimezoneAwareDatetime(a.startDatetime) : new Date(a.date || 0);
                const dateB = b.startDatetime ? parseTimezoneAwareDatetime(b.startDatetime) : new Date(b.date || 0);
                return dateA.getTime() - dateB.getTime();
            });
        
        console.log('[getFutureSessions] Future sessions count:', futureSessions.length);
        
        return futureSessions;
    } catch (error) {
        console.error('[getFutureSessions] Failed to fetch future sessions:', error);
        return [];
    }
}